// Publisher BYO auth adapter registry. The publisher registers a credential-
// shaped AuthAdapter via ezsubscriptions.initialize({ auth }); the SDK reads the
// registered adapter when resolving identity for checkout and content access.
// ezAuth mode builds its adapter internally in a later phase.

import type { AuthAdapter, AuthMode } from '@/types';
import { logError } from '@/sdkConsole';

let registeredAdapter: AuthAdapter | null = null;

// isAuthAdapter reports whether value satisfies the full AuthAdapter contract:
// the navigation methods plus at least one identity method. Identity is
// either getUserEmail (plaintext email) or getIdentityToken (SM-minted token
// that keeps the email off the page) — one of the two is required, not both.
function isAuthAdapter(value: unknown): value is AuthAdapter {
  if (!value || typeof value !== 'object') return false;
  const adapter = value as Partial<AuthAdapter>;
  const hasIdentity = typeof adapter.getUserEmail === 'function' || typeof adapter.getIdentityToken === 'function';
  return hasIdentity
    && typeof adapter.goToLogin === 'function'
    && typeof adapter.goToCreateAccount === 'function';
}

// registerAdapter stores the publisher-supplied BYO adapter. An incomplete
// adapter is logged and rejected rather than stored, so a later valid
// initialize can replace it and the prior adapter (if any) is left untouched.
export function registerAdapter(auth: unknown): void {
  if (!isAuthAdapter(auth)) {
    logError('initialize requires an auth adapter with goToLogin, goToCreateAccount, and at least one of getUserEmail or getIdentityToken');
    return;
  }
  registeredAdapter = auth;
}

export function getAdapter(): AuthAdapter | null {
  return registeredAdapter;
}

// validateAuthAdapter reports whether checkout may proceed for the domain's auth
// mode. A BYO domain requires a complete registered adapter; a missing one is
// logged and blocks checkout until the publisher configures it. Other modes
// (ezauth/default) are not gated here.
export function validateAuthAdapter(authMode?: AuthMode): boolean {
  if (authMode !== 'byo') return true;
  if (registeredAdapter) return true;
  logError('this domain is configured for publisher (BYO) auth but ezsubscriptions.initialize({ auth }) was not called; checkout is blocked until it is configured');
  return false;
}
